import { useState, useEffect } from 'react';
import { HiEnvelope, HiCheckCircle, HiXMark, HiArrowPath } from 'react-icons/hi2';
import { useAuthStore } from '@/stores/authStore';
import './EmailValidationBanner.css';

const MKEY_API = 'https://mkey-api-production.fidelismadboat.workers.dev';

interface EmailStatus {
  verified: boolean;
  email: string;
  deadline: number | null;
  time_remaining: number;
}

export function EmailValidationBanner() {
  const { token, refreshUser } = useAuthStore();
  const [emailStatus, setEmailStatus] = useState<EmailStatus | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  // Fetch email status on mount
  useEffect(() => {
    if (!token) return;

    const fetchEmailStatus = async () => {
      try {
        const response = await fetch(`${MKEY_API}/invites/email-status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          setEmailStatus(data);
        }
      } catch (err) {
        console.error('[EmailValidationBanner] Error fetching status:', err);
      }
    };

    fetchEmailStatus();
  }, [token]);

  // Countdown timer
  useEffect(() => {
    if (!emailStatus?.deadline || emailStatus.verified) return;

    const updateTimer = () => {
      const now = Date.now();
      const remaining = emailStatus.deadline! - now;

      if (remaining <= 0) {
        setTimeRemaining('Expirado');
        return;
      }

      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m restantes`);
      } else {
        setTimeRemaining(`${minutes}m restantes`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [emailStatus?.deadline, emailStatus?.verified]);

  // Handle OTP verification
  const handleVerify = async () => {
    if (!otpCode.trim() || otpCode.length !== 6) {
      setError('Digite o codigo de 6 digitos');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const response = await fetch(`${MKEY_API}/invites/verify-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: otpCode }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Email verificado com sucesso!');
        setEmailStatus({ ...emailStatus!, verified: true });
        // Refresh user data
        setTimeout(() => refreshUser(), 1000);
      } else {
        setError(data.error || 'Codigo invalido');
      }
    } catch (err) {
      setError('Erro ao verificar. Tente novamente.');
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle resend OTP
  const handleResend = async () => {
    setIsResending(true);
    setError(null);

    try {
      const response = await fetch(`${MKEY_API}/invites/resend-verification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(data.message || 'Codigo reenviado!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Erro ao reenviar');
      }
    } catch (err) {
      setError('Erro ao reenviar. Tente novamente.');
    } finally {
      setIsResending(false);
    }
  };

  // Don't show if no status or already verified
  if (!emailStatus || emailStatus.verified) {
    return null;
  }

  // Success animation
  if (success && emailStatus.verified) {
    return (
      <div className="email-validation-banner success">
        <HiCheckCircle className="success-icon" />
        <span>{success}</span>
      </div>
    );
  }

  return (
    <div className={`email-validation-banner ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="banner-header">
        <div className="banner-title">
          <HiEnvelope className="banner-icon" />
          <span>Valide seu email</span>
          <span className="banner-timer">{timeRemaining}</span>
        </div>
        <button
          className="banner-toggle"
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? 'Expandir' : 'Minimizar'}
        >
          {isCollapsed ? <HiEnvelope /> : <HiXMark />}
        </button>
      </div>

      {!isCollapsed && (
        <div className="banner-content">
          <p className="banner-text">
            Digite o codigo de 6 digitos enviado para <strong>{emailStatus.email}</strong>
          </p>

          <div className="otp-input-row">
            <input
              type="text"
              className="otp-input"
              placeholder="000000"
              maxLength={6}
              value={otpCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setOtpCode(value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleVerify();
              }}
            />
            <button
              className="verify-btn"
              onClick={handleVerify}
              disabled={isVerifying || otpCode.length !== 6}
            >
              {isVerifying ? 'Verificando...' : 'Verificar'}
            </button>
          </div>

          {error && <p className="banner-error">{error}</p>}
          {success && !emailStatus.verified && <p className="banner-success">{success}</p>}

          <button
            className="resend-btn"
            onClick={handleResend}
            disabled={isResending}
          >
            <HiArrowPath className={isResending ? 'spinning' : ''} />
            {isResending ? 'Reenviando...' : 'Reenviar codigo'}
          </button>
        </div>
      )}
    </div>
  );
}
